import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

/**
 * F1 — the ONLY sanctioned way to render model-supplied text in the Ask app.
 * Mirrors the Contribute app's Markdown component
 * (portal-source/src/components/Markdown.tsx) and its sanitize policy:
 * react-markdown never uses dangerouslySetInnerHTML, and rehype-sanitize (default
 * schema) strips any raw/embedded HTML to a safe allowlist, so hostile markup in
 * a model answer cannot execute. Numbered/bulleted lists, paragraphs, bold, and
 * code render; raw HTML does not pass through.
 *
 * The per-element styling matches the chat surface it replaced; the sanitize
 * policy is identical to Contribute so both doors render model text the same
 * (safe) way.
 */
export function Markdown({ children }: { children: string }) {
  return (
    <div className="md">
      <ReactMarkdown
        rehypePlugins={[rehypeSanitize]}
        components={{
          h1: ({ children }) => <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem' }}>{children}</h1>,
          h2: ({ children }) => <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '1rem', marginBottom: '0.5rem' }}>{children}</h2>,
          h3: ({ children }) => <h3 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.75rem', marginBottom: '0.25rem' }}>{children}</h3>,
          p: ({ children }) => <p style={{ marginBottom: '0.75rem' }}>{children}</p>,
          ul: ({ children }) => <ul style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem' }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem' }}>{children}</ol>,
          li: ({ children }) => <li style={{ marginBottom: '0.25rem' }}>{children}</li>,
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code style={{ background: 'var(--bg-tertiary)', padding: '0.125rem 0.375rem', borderRadius: '4px', fontSize: '0.875rem' }}>{children}</code>
            ) : (
              <code style={{ display: 'block', background: 'var(--bg-tertiary)', padding: '0.75rem', borderRadius: '8px', fontSize: '0.875rem', overflowX: 'auto', marginBottom: '0.75rem' }}>{children}</code>
            );
          },
          strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
          hr: () => <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '1rem 0' }} />,
        }}
      >
        {children || ''}
      </ReactMarkdown>
    </div>
  );
}
